import { FC } from 'react'
import { useFieldArray } from 'react-hook-form'

import { Icon } from '@/common/components/Icon'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import FormGroup from '@/core/components/FormGroup'
import { ProviderFieldsProps } from './types'

export const PulsarFields: FC<ProviderFieldsProps> = ({
  index,
  control,
  register,
  errors,
}) => {
  const {
    fields: chainFields,
    append: appendChain,
    remove: removeChain,
  } = useFieldArray({
    control,
    name: `paymentProviders.${index}.chains` as `paymentProviders.${number}.chains`,
  })

  const {
    fields: tokenFields,
    append: appendToken,
    remove: removeToken,
  } = useFieldArray({
    control,
    name: `paymentProviders.${index}.tokens` as `paymentProviders.${number}.tokens`,
  })

  return (
    <>
      <Card className="flex w-full flex-col gap-3 bg-monochrome-3-60 p-[15px]">
        <Label className="!text-title-xs text-monochrome-1">Chain</Label>
        <div className="space-y-3">
          {chainFields.map((field, chainIndex) => (
            <Card
              key={field.id}
              className="flex flex-col gap-4 border border-monochrome-120 bg-monochrome-3-60 p-[15px]"
            >
              <FormGroup
                label="Chain Name"
                error={
                  errors?.[index]?.chains?.[chainIndex]?.chainName?.message
                }
                labelProps={{ htmlFor: `chainName-${index}-${chainIndex}` }}
                className="flex-1"
              >
                <Input
                  id={`chainName-${index}-${chainIndex}`}
                  isInvalid={
                    !!errors?.[index]?.chains?.[chainIndex]?.chainName?.message
                  }
                  {...register(
                    `paymentProviders.${index}.chains.${chainIndex}.chainName`,
                  )}
                  placeholder="Chain Name"
                />
              </FormGroup>
              <FormGroup
                label="Chain ID"
                error={errors?.[index]?.chains?.[chainIndex]?.chainId?.message}
                labelProps={{ htmlFor: `chainId-${index}-${chainIndex}` }}
                className="flex-1"
              >
                <Input
                  id={`chainId-${index}-${chainIndex}`}
                  isInvalid={
                    !!errors?.[index]?.chains?.[chainIndex]?.chainId?.message
                  }
                  {...register(
                    `paymentProviders.${index}.chains.${chainIndex}.chainId`,
                  )}
                  placeholder="Chain ID"
                />
              </FormGroup>
              <FormGroup
                label="Tax Percent"
                error={
                  errors?.[index]?.chains?.[chainIndex]?.taxPercent?.message
                }
                labelProps={{ htmlFor: `taxPercent-${index}-${chainIndex}` }}
                className="flex-1"
              >
                <Input
                  id={`taxPercent-${index}-${chainIndex}`}
                  isInvalid={
                    !!errors?.[index]?.chains?.[chainIndex]?.taxPercent?.message
                  }
                  {...register(
                    `paymentProviders.${index}.chains.${chainIndex}.taxPercent`,
                  )}
                  placeholder="Tax Percent"
                />
              </FormGroup>
              <div className="w-full flex items-center justify-between">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => removeChain(chainIndex)}
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
                <div className="flex items-center gap-4 [&>*]:w-[154px]">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      appendToken({ tokenSymbol: '', exchangeRate: '' })
                    }
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() =>
                      appendToken({ tokenSymbol: '', exchangeRate: '' })
                    }
                  >
                    Save
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Card>
      <div className="w-full flex items-center justify-center mt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() =>
            appendChain({ chainName: '', chainId: '', taxPercent: '' })
          }
        >
          Add New Chain
          <Icon
            icon="plus"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          />
        </Button>
      </div>

      <Card className="flex w-full flex-col gap-3 bg-monochrome-3-60 p-[15px]">
        <Label className="!text-title-xs text-monochrome-1">Token</Label>
        <div className="space-y-3">
          {tokenFields.map((field, tokenIndex) => (
            <Card
              key={field.id}
              className="flex flex-col gap-4 border border-monochrome-120 bg-monochrome-3-60 p-[15px]"
            >
              <FormGroup
                label="Token Symbol"
                error={
                  errors?.[index]?.tokens?.[tokenIndex]?.tokenSymbol?.message
                }
                labelProps={{
                  htmlFor: `tokenSymbol-${index}-${tokenIndex}`,
                }}
                className="flex-1"
              >
                <Input
                  id={`tokenSymbol-${index}-${tokenIndex}`}
                  isInvalid={
                    !!errors?.[index]?.tokens?.[tokenIndex]?.tokenSymbol
                      ?.message
                  }
                  {...register(
                    `paymentProviders.${index}.tokens.${tokenIndex}.tokenSymbol`,
                  )}
                  placeholder="Token Symbol"
                />
              </FormGroup>
              <FormGroup
                label="Exchange Rate"
                error={
                  errors?.[index]?.tokens?.[tokenIndex]?.exchangeRate?.message
                }
                labelProps={{
                  htmlFor: `exchangeRate-${index}-${tokenIndex}`,
                }}
                className="flex-1"
              >
                <Input
                  id={`exchangeRate-${index}-${tokenIndex}`}
                  isInvalid={
                    !!errors?.[index]?.tokens?.[tokenIndex]?.exchangeRate
                      ?.message
                  }
                  {...register(
                    `paymentProviders.${index}.tokens.${tokenIndex}.exchangeRate`,
                  )}
                  placeholder="Exchange Rate"
                />
              </FormGroup>
              <div className="w-full flex items-center justify-between">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => removeToken(tokenIndex)}
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
                <div className="flex items-center gap-4 [&>*]:w-[154px]">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => removeToken(tokenIndex)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => removeToken(tokenIndex)}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Card>
      <div className="w-full flex items-center justify-center mt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() => appendToken({ tokenSymbol: '', exchangeRate: '' })}
        >
          Add New Token
          <Icon
            icon="plus"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          />
        </Button>
      </div>
    </>
  )
}
